import {
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	RowPreparedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	filter,
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	Subject,
	takeUntil,
	tap,
	withLatestFrom,
	zip,
} from 'rxjs';

import { PROFILE_ID } from '../../../core/constants';
import { ElementPermission } from '../../../core/domain/permission-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { ColumnModelItemEntity } from '../../domain/column-model-item.entity';
import { SubscriptionErrorKey } from '../../domain/diagnostic-errors.entity';
import { FilterModelItemEntity } from '../../domain/filter-model-item.entity';
import { ProfileItemEntity } from '../../domain/profile-item.entity';
import { ProfileListEntity } from '../../domain/profile-list.entity';
import {
	ColumnStatus,
	DatablockColumn,
	ProfileConfigType,
	SubscriptionStatus,
} from '../../domain/subscription.types';
import { HighlightProvider } from '../../providers/highlight.provider';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	createNewProfile,
	navigateToSubscriptionSimulationWithSelectedProfile,
	openDeleteProfileConfirmPopup,
	SubscriptionSelector,
	updateProfile,
} from '../../store';
import {
	ProfileConfigAction,
	ProfileConfigModalComponent,
} from './profile-config-modal/profile-config-modal.component';
import { ProfileListProvider } from './profile-list.provider';

export type ProfileColumModelDetails = {
	label: string;
	columns: Set<DatablockColumn>;
};

type SubscriptionOverlayProfile = {
	profileId: string | undefined;
};

type Mode = 'PROJECT_STANDALONE' | 'STANDALONE' | 'SIMULATION';

@Component({
	selector: 'app-profiles',
	templateUrl: './profiles.component.html',
	styleUrls: ['./profiles.component.scss'],
	inputs: ['profiles', 'columnModels', 'mode', 'selectedId'],
	providers: [ProfileListProvider],
})
export class ProfilesComponent extends DcBaseComponent implements OnInit {
	protected readonly ActionIcons = ActionIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ColumnStatus = ColumnStatus;
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly ElementPermission = ElementPermission;
	protected readonly SubscriptionStatus = SubscriptionStatus;

	@ViewChild(DxDataGridComponent)
	public grid: DxDataGridComponent | null = null;

	@Output()
	public reloadProfiles = new EventEmitter<void>();

	public currentlyHovered = -1;
	public profileActions: Array<PanelAction> = [];

	public vo$: Observable<{
		subscriptionStatus: SubscriptionStatus;
		profileList: ProfileListEntity;
		profilesColumnModels: Map<string, ProfileColumModelDetails>;
		openedOverlayProfile: SubscriptionOverlayProfile;
		mode: Mode;
		userPermissions: Set<ElementPermission>;
	}>;

	private readonly openedOverlayProfile$ =
		new BehaviorSubject<SubscriptionOverlayProfile>({
			profileId: undefined,
		});

	private readonly isGridReadySubject$ = new Subject<boolean>();
	private readonly profilesColumnModels$ = new ReplaySubject<
		Map<string, ProfileColumModelDetails>
	>(1);

	public constructor(
		@Optional() private readonly subscriptionProvider: SubscriptionProvider,
		@Optional() private readonly highlightProvider: HighlightProvider,
		private readonly profileListProvider: ProfileListProvider,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly selector: SubscriptionSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'profiles';

		const profileList$ = this.toObservable<Map<string, ProfileItemEntity>>(
			'profiles'
		).pipe(
			map((profiles) => this.profileListProvider.setListElements(profiles))
		);

		this.vo$ = combineAll({
			subscriptionStatus: this.selector.getSubscriptionStatus$(),
			profileList: profileList$,
			profilesColumnModels: this.profilesColumnModels$,
			openedOverlayProfile: this.openedOverlayProfile$,
			mode: this.toObservable<Mode>('mode'),
			userPermissions: this.selector.getSubscriptionUserPermissions$(),
		});
	}

	public override ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public selectProfile(event: SelectionChangedEvent): void {
		if (event.selectedRowKeys.length === 0) {
			return;
		}
		this.subscriptionProvider.removeError(SubscriptionErrorKey.NoSelectProfile);
	}

	public refresh(): void {
		this.reloadProfiles.emit();
	}

	public async simulateProfile(item: ProfileItemEntity): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.selector.getSubscriptionId$()
		);
		this.store.dispatch(
			navigateToSubscriptionSimulationWithSelectedProfile({
				subscriptionId,
				profile: item.id,
			})
		);
	}

	public async openProfileModal(item: ProfileItemEntity | null): Promise<void> {
		let title = '';
		let subTitle = '';
		let profileConfig;
		let action;

		if (item instanceof ProfileItemEntity) {
			title = $localize`:i18n=@@subscription.profile.update.title:`;
			subTitle = $localize`:i18n=@@subscription.profile.update.subTitle:`;
			profileConfig = item;
			action = ProfileConfigAction.Update;
		} else {
			title = $localize`:i18n=@@subscription.profile.create.title:`;
			subTitle = $localize`:i18n=@@subscription.profile.create.subTitle:`;
			profileConfig = ProfileItemEntity.build();
			action = ProfileConfigAction.Create;
		}

		const modalRef = this.modalService.openModal<{
			profileConfig: ProfileItemEntity;
			action: ProfileConfigAction;
		}>(
			ProfileConfigModalComponent,
			{
				title,
				subTitle,
				profileConfig,
				action,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const {
			metadata,
			columnModels: selectedColumnModels,
			filterModels: selectedFilterModels,
		} = modalResult as ProfileConfigType;

		const initialColumnModels = await firstValueFrom(
			this.selector.getSubscriptionColumnModels$()
		);
		const initialFilterModels = await firstValueFrom(
			this.selector.getSubscriptionFilterModels$()
		);

		const [firstSelectedColumnModel] = selectedColumnModels;

		const columnModel = initialColumnModels.get(firstSelectedColumnModel.id)
			? firstSelectedColumnModel.id
			: firstSelectedColumnModel;

		const filters = selectedFilterModels.reduce((acc, curr) => {
			if (initialFilterModels.get(curr.id)) {
				acc.push(curr.id);
				return acc;
			}
			acc.push(curr);
			return acc;
		}, new Array<FilterModelItemEntity | string>());

		if (item instanceof ProfileItemEntity) {
			return this.store.dispatch(
				updateProfile({
					profile: metadata,
					columnModel,
					filterModels: filters,
				})
			);
		}

		this.store.dispatch(
			createNewProfile({
				profile: metadata,
				columnModel,
				filterModels: filters,
			})
		);
	}

	public deleteProfile(profile: ProfileItemEntity): void {
		this.store.dispatch(
			openDeleteProfileConfirmPopup({
				profileId: profile.id,
				profileLabel: profile.label,
			})
		);
	}

	public getSelectedProfiles(): Array<ProfileItemEntity> {
		if (!this.grid) {
			return [];
		}

		return this.grid.instance.getSelectedRowsData();
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onGridReady(): void {
		this.isGridReadySubject$.next(true);
	}

	public onRowPrepared(event: RowPreparedEvent): void {
		if (event.rowType !== 'data' || !this.highlightProvider) {
			return;
		}

		const selectedProfile = event.data as ProfileItemEntity;
		event.rowElement.setAttribute(`data-${PROFILE_ID}`, selectedProfile.id);

		const element = event.rowElement;

		element.onmouseenter = (): void => {
			this.highlightProvider.setRelatedElementsHighlight(
				'PROFILE',
				selectedProfile,
				true
			);
		};

		element.onmouseleave = (): void => {
			this.highlightProvider.setRelatedElementsHighlight(
				'PROFILE',
				selectedProfile,
				false
			);
		};
	}

	public async openColumnsPopover(
		event: Event,
		profileId: string
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlayProfile$);

		if (previous.profileId === profileId) {
			this.openedOverlayProfile$.next({
				...previous,
				profileId: undefined,
			});
			return;
		}

		this.openedOverlayProfile$.next({
			...previous,
			profileId,
		});
	}

	public closeColumnsPopover(): void {
		this.openedOverlayProfile$.next({
			profileId: undefined,
		});
	}

	private handleSubscription(): void {
		combineAll({
			profiles: this.toObservable<Map<string, ProfileItemEntity>>('profiles'),
			columnModels:
				this.toObservable<Map<string, ColumnModelItemEntity>>('columnModels'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ profiles, columnModels }) => {
					if (!profiles || !columnModels) {
						return this.profilesColumnModels$.next(
							Map<string, ProfileColumModelDetails>()
						);
					}

					const profilesColumnModels = profiles.reduce((acc, profile) => {
						const columnModel = columnModels.find(
							(cm) => cm.id === profile.columnModelId
						);

						if (!columnModel) {
							return acc;
						}

						return acc.set(profile.id, {
							label: columnModel.label,
							columns: columnModel.columns.map<DatablockColumn>((col) => ({
								id: col.id,
								label: col.alias,
								type: col.columnType,
								isList: col.isList,
								isHashed: col.isHashed,
							})),
						});
					}, Map<string, ProfileColumModelDetails>());

					this.profilesColumnModels$.next(profilesColumnModels);
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<Mode>('mode')),
				filter(([, mode]) => mode === 'STANDALONE'),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		zip(
			this.isGridReadySubject$,
			this.toObservable<string>('selectedId'),
			this.toObservable<Map<string, ProfileItemEntity>>('profiles')
		)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<string>('mode')),
				filter(([, mode]) => mode !== 'STANDALONE'),
				tap(([[, selected]]) => {
					if (selected === '') {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.noSelectProfile:`,
							SubscriptionErrorKey.NoSelectProfile
						);
					} else {
						this.grid?.instance.selectRows([selected], false);
					}
				})
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.Add];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.profileActions = [
			{
				order: 0,
				command: (): Promise<void> => this.openProfileModal(null),
				iconName: actionMap.get(ActionIcons.Add)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@general.action.new:`,
			},
		];
	}
}
